import { createRouter, createMemoryHistory, createWebHistory, useRouter } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'

import NotFound from '@/modules/public/NotFound.vue'
import Signin from '@/modules/public/Signin/Index.vue'
import SignUp from '@/modules/public/SignUp/Index.vue'
import SignUpContractor from '@/modules/public/SignUp/contractor/Index.vue'
import SignUpCompany from '@/modules/public/SignUp/company/Index.vue'
import SignUpDoctorApp from '@/modules/public/SignUpDoctorApp/Index.vue'
import ForgotPassword from '@/modules/public/ForgotPassword/Index.vue'
import ManageInviteDoctor from '@/modules/public/ManageInviteDoctor/Index.vue'
import RecoveryPasswordDoctor from '@/modules/public/RecoveryPasswordDoctor/Index.vue'
import CreateSchedule from '@/components/ManageScale/Schedule/CreateSchedule.vue'
import Dashboard from '@/modules/company/Dashboard/Index.vue'

import NewComponents from '@/modules/company/NewComponents/Index.vue'
import ManageScale from '@/modules/company/ManageScale/Index.vue'
import Doctors from '@/modules/company/Doctors/Index.vue'
import PolicyLanding from '@/modules/company/Policy/Index.vue'
import Policies from '@/modules/company/Policy/DsPolicy/Index.vue'
import ManageDoctorAuthorizationPolicie from '@/modules/company/Policy/DsPolicy/components/ManageDoctorAuthorizationDsPolicies/ManageDoctorAuthorizationPolicies.vue'
import ComingSoon from '@/modules/company/Policy/ComingSoon.vue'
import QualificationTransfer from '@/modules/company/Policy/QualificationTransfer/Index.vue'
import QualificationDocumental from '@/modules/company/Policy/QualificationDocumental/Index.vue'
import Users from '@/modules/company/Users/Index.vue'
import Profile from '@/modules/company/Profile/Index.vue'
import AccessManager from '@/modules/company/Profile/AccessManager/Index.vue'
import Consult from '@/modules/company/Consult/index.vue'
import Reports from '@/modules/company/Reports/index.vue'
import AuditoriumExceptionalities from '@/modules/company/AuditoriumExceptionalities/index.vue'
import Approvals from '@/modules/company/Approvals/Index.vue'
import ApprovalsQueuePage from '@/modules/company/Approvals/Hub/ApprovalsQueuePage.vue'
import {
  approvalsQueueAuthorizations,
  findApprovalsQueue,
  resolveApprovalsQueueGuard,
  resolveApprovalsTabRedirect
} from '@/modules/company/Approvals/Hub/queues'
import type { ApprovalsQueueKey } from '@/modules/company/Approvals/Hub/queues'
import DsAttendanceAwaitingAudit from '@/modules/company/Approvals/DsAttendance/components/DsAttendanceAwaitingAudit.vue'
import DsAttendanceAwaitingShipment from '@/modules/company/Approvals/DsAttendance/components/DsAttendanceAwaitingShipment.vue'
import HandoverEvaluation from '@/modules/company/Approvals/HandoverApprovals/HandoverEvaluation.vue'
import HandoverDetails from '@/modules/company/Approvals/HandoverApprovals/HandoverDetails.vue'

import PATHS from './paths'
import { useAuthStore } from '@/pinia-store/useAuthStore'
import { usePermissionsStore } from '@/pinia-store/usePermissionsStore'
import { initClarity, identifyUser, stopClarity, trackPage } from '@/config/clarity'
import isSSR from '@/_base/isSSR'
import { Authorizations } from '@/types/authorization'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import {
  AnnouncementManager,
  TermsManager,
  CompanyGeneralAdmin,
  DoctorDocumentReader,
  DsAttendanceQueueReader,
  EffectiveCheckRecalculator,
  HealthUnitBasicReader,
  HealthUnitEditor,
  ManagementReader,
  NpsAdmin,
  NpsEvaluator,
  NpsReader,
  PostStationPanelReader,
  PostStationPolicies,
  ScheduleAdvancedReader,
  ScheduleBasicReader,
  ScheduleContentAdmin,
  StationAdvancedReader,
  StationBasicReader
} from './permissionsGroupByRouter'
import NotAuthorized from '@/modules/company/NotAuthorized/NotAuthorized.vue'
// Import estático: a tela aparece justamente quando a rede falhou, e um chunk
// lazy poderia não carregar.
import PermissionsUnavailable from '@/modules/company/PermissionsUnavailable/PermissionsUnavailable.vue'
import { PERMISSIONS_UNAVAILABLE_ROUTE_NAME } from '@/modules/company/PermissionsUnavailable/retryTarget'
import { NOT_FOUND_ROUTE_NAME } from '@/utils/layout'
import { restoreViewportOutside } from '@/utils/classicViewport'
import { isChunkLoadError } from '@/utils/chunkLoadError'
import { useAppVersionCheck } from '@/hooks/useAppVersionCheck'
import { isAffiliateAuthenticated, isCompanyAuthenticated } from '@/utils/storage'
import { affiliateRoutes } from '@/modules/affiliate/routes'
import { resolveAffiliateGuard } from '@/modules/affiliate/guard'
import type { AffiliatePage } from '@/modules/affiliate/labels'
declare module 'vue-router' {
  export interface RouteMeta {
    authorizations?: Authorizations
    fullHeight?: boolean
    hideFooter?: boolean
    pageTitle?: string
    layout?: 'default' | 'empty' | 'affiliate'
    requiresCompanyUserAuth?: boolean
    /** Portal do Afiliado Antecipe: exige @ME:affiliate_token (ver modules/affiliate/guard.ts). */
    requiresAffiliateAuth?: boolean
    /** Tela do portal (títulos e menu do layout affiliate). */
    affiliatePage?: AffiliatePage
    /** "Ver como" da equipe BNG: mesmo layout, dados de /admin/antecipe-affiliates/:id. */
    affiliateImpersonation?: boolean
    /** Chave da fila de aprovação (Approvals/Hub/queues.ts) nas rotas /aprovacoes/<fila>. */
    approvalsQueue?: string
    /**
     * Rota que `findFirstAccessibleRoute` NUNCA escolhe como destino do
     * redirecionamento de acesso negado (telas de backoffice, só por link direto).
     */
    excludeFromAccessFallback?: boolean
    /** Rota em que o Clarity não age nem com sessão de empresa (ex.: token na URL). */
    skipAnalytics?: boolean
    /** Tela sem operador (ex.: Modo TV): ao sair versão nova, recarrega sozinha em vez de avisar. */
    autoReloadOnUpdate?: boolean
  }
}

export function useRouteMetaUtils() {
  const router = useRouter()

  const getAuthorizationsByPath = (path: string): Authorizations | undefined => {
    if (!router) {
      console.warn(
        'Vue Router não foi instanciado ou a função foi chamada fora do contexto de setup.'
      )
    }

    try {
      const matchedRoute = router.resolve(path)

      return matchedRoute.meta.authorizations
    } catch (e) {
      console.error(`Erro ao resolver a rota para o caminho: ${path}`, e)
      return undefined
    }
  }

  const hasMetaAuthorization = (path: string): boolean => {
    const auths = getAuthorizationsByPath(path)
    return Array.isArray(auths) && auths.length > 0
  }

  return {
    getAuthorizationsByPath,
    hasMetaAuthorization
  }
}

const isServer = typeof window === 'undefined'

const history = isServer ? createMemoryHistory() : createWebHistory()

// Rota incluida condicionalmente pela flag de build VUE_APP_ENABLED_POST_STATION —
// sem a flag, a rota nao existe (NotFound).
const postStationRoute = {
  path: PATHS.POST_STATION,
  name: 'pos-plantao',
  component: () => import('@/modules/company/PostStation/Index.vue'),
  meta: { requiresCompanyUserAuth: true, authorizations: [...PostStationPanelReader] }
}

// Gestão (relatórios mensal e anual): rota só existe com VUE_APP_ENABLED_MANAGEMENT,
// a mesma flag do item no menu. Sem a flag, a URL cai no NotFound.
const managementRoute = {
  path: PATHS.MANAGEMENT,
  name: 'gestao',
  component: () => import('@/modules/company/Management/Index.vue'),
  meta: { requiresCompanyUserAuth: true, authorizations: [...ManagementReader] }
}

// Central de vagas (Onda 2.5a): rota só existe com VUE_APP_ENABLED_DASHBOARD_VACANCY_CENTER —
// sem a flag, a URL cai no NotFound. Independe da flag da Dashboard V2.
const vacancyCenterRoute = {
  path: PATHS.VACANCY_CENTER,
  name: 'central-de-vagas',
  component: () => import('@/modules/company/VacancyCenter/Index.vue'),
  meta: {
    requiresCompanyUserAuth: true,
    authorizations: [...StationBasicReader],
    pageTitle: 'Central de vagas'
  }
}
// Comunicados (PoC Novo Hamburgo, V-b): rotas só existem com VUE_APP_ENABLED_ANNOUNCEMENTS —
// sem a flag, a URL cai no NotFound. `/novo` vem antes de `/:announcementId`.
const announcementMeta = (pageTitle: string) => ({
  requiresCompanyUserAuth: true,
  authorizations: [...AnnouncementManager],
  pageTitle
})
const announcementRoutes: RouteRecordRaw[] = [
  {
    path: PATHS.ANNOUNCEMENTS,
    name: 'comunicados',
    component: () => import('@/modules/company/Announcements/Index.vue'),
    meta: announcementMeta('Comunicados')
  },
  {
    path: PATHS.ANNOUNCEMENT_NEW,
    name: 'comunicados-novo',
    component: () => import('@/modules/company/Announcements/NewAnnouncement.vue'),
    meta: announcementMeta('Novo comunicado')
  },
  {
    path: PATHS.ANNOUNCEMENT_DETAIL,
    name: 'comunicados-detalhe',
    component: () => import('@/modules/company/Announcements/AnnouncementDetail.vue'),
    meta: announcementMeta('Comunicado')
  }
]
// Termos de ciência (PRD26): rotas só existem com VUE_APP_ENABLED_TERMS. `versoes/:id` e
// `:modality/nova-versao` têm dois segmentos, `historico` um só e
// `versoes/:id/assinaturas` três: não há conflito de ordem. Sem `excludeFromAccessFallback`
// de propósito: quem cai no fallback de acesso com permissão de termos chega a eles.
const termsMeta = (pageTitle: string) => ({
  requiresCompanyUserAuth: true,
  authorizations: [...TermsManager],
  pageTitle
})
const termsRoutes: RouteRecordRaw[] = [
  {
    path: PATHS.TERMS,
    name: 'termos-de-ciencia',
    component: () => import('@/modules/company/Terms/Index.vue'),
    meta: termsMeta('Termos de ciência')
  },
  {
    path: PATHS.TERMS_HISTORY,
    name: 'termos-de-ciencia-historico',
    component: () => import('@/modules/company/Terms/TermsHistory.vue'),
    meta: termsMeta('Histórico de versões')
  },
  {
    path: PATHS.TERM_VERSION_DETAIL,
    name: 'termos-de-ciencia-versao',
    component: () => import('@/modules/company/Terms/TermVersionDetail.vue'),
    meta: termsMeta('Termo de ciência')
  },
  {
    path: PATHS.TERM_SIGNATURES,
    name: 'termos-de-ciencia-assinaturas',
    component: () => import('@/modules/company/Terms/TermSignatures.vue'),
    meta: termsMeta('Histórico de assinaturas')
  },
  {
    path: PATHS.TERM_NEW_VERSION,
    name: 'termos-de-ciencia-nova-versao',
    component: () => import('@/modules/company/Terms/NewTermVersion.vue'),
    meta: termsMeta('Nova versão do termo')
  }
]
// Tela Demanda (Onda 2.2): rotas incluídas só com `enabledDashboardDemand` (V2 && DEMAND);
// sem a flag não existem (NotFound). Import lazy para não carregar a tela no bundle inicial.
const demandMeta = () => ({
  requiresCompanyUserAuth: true,
  authorizations: [...StationBasicReader]
})
const demandRoutes: RouteRecordRaw[] = [
  {
    path: PATHS.DEMAND,
    name: 'demanda',
    component: () => import('@/modules/company/Demand/DemandPage.vue'),
    meta: demandMeta()
  },
  {
    path: PATHS.DEMAND_TARGETS,
    name: 'demanda-quadro-meta',
    component: () => import('@/modules/company/Demand/DemandTargetsPage.vue'),
    meta: demandMeta()
  },
  {
    path: PATHS.DEMAND_CHANGES,
    name: 'demanda-o-que-mudou',
    component: () => import('@/modules/company/Demand/DemandChangesPage.vue'),
    meta: demandMeta()
  }
]

// Modo TV público do Mapa da unidade (Onda 2.4, spec §5.3): só com `enabledDashboardMap`;
// sem login (sem `requiresCompanyUserAuth` → o `beforeResolve` só chama `next()`), layout
// `empty` (sem sidebar) e fora do Clarity — o token do link está na URL.
const unitMapTvRoute: RouteRecordRaw = {
  path: PATHS.UNIT_MAP_TV,
  name: 'UnitMapTv',
  component: () => import('@/modules/public/UnitMapTv/Index.vue'),
  meta: { layout: 'empty', skipAnalytics: true, autoReloadOnUpdate: true }
}

// Link público do convite de alocação (acceptance-terms, spec §12, D52): só com
// `enabledAllocationInvite` (D91 — sem a flag a URL cai no NotFound); sem login, layout
// `empty` e fora do Clarity — o token do link está na URL.
const allocationInviteAcceptRoute: RouteRecordRaw = {
  path: PATHS.ALLOCATION_INVITE_ACCEPT,
  name: 'AllocationInviteAccept',
  component: () => import('@/modules/public/AllocationInviteAccept/Index.vue'),
  meta: { layout: 'empty', skipAnalytics: true }
}

// Rota de uma fila do hub de aprovações: autorização declarada no registro da fila
// (default: a da home), guard que devolve ao shell de abas quando o hub está desligado.
const approvalsQueueRoute = (
  path: string,
  name: string,
  key: ApprovalsQueueKey
): RouteRecordRaw => ({
  path,
  name,
  component: ApprovalsQueuePage,
  meta: {
    requiresCompanyUserAuth: true,
    authorizations: [...approvalsQueueAuthorizations(findApprovalsQueue(key))],
    approvalsQueue: key
  },
  beforeEnter: (to) => resolveApprovalsQueueGuard(key, to, useFeatureFlags().flags)
})

export const routes: RouteRecordRaw[] = [
  {
    path: PATHS.SIGNIN,
    name: 'signin',
    component: Signin,
    meta: { layout: 'empty' }
  },
  {
    path: PATHS.RECOVER_PASSWORD,
    name: 'recovery-password',
    component: RecoveryPasswordDoctor,
    meta: { layout: 'empty' }
  },
  {
    path: PATHS.FORGOTPASSWORD,
    name: 'forgot-password',
    component: ForgotPassword,
    meta: { layout: 'empty' }
  },
  {
    path: PATHS.MANAGE_DOCTOR_INVITE,
    name: 'convite',
    component: ManageInviteDoctor,
    // Rota pública por design: a página trata signup/login/aceite do convite
    // internamente — médico convidado chega aqui sem sessão no ME.
    meta: { layout: 'empty' }
  },
  {
    path: PATHS.SIGNUP,
    name: 'signup',
    component: SignUp,
    meta: { layout: 'empty' }
  },
  {
    path: PATHS.SIGNUPCONTRACTOR,
    name: 'signup-contractor',
    component: SignUpContractor,
    meta: { layout: 'empty' }
  },
  {
    path: PATHS.SIGNUPCOMPANY,
    name: 'signup-company',
    component: SignUpCompany,
    meta: { layout: 'empty' }
  },
  {
    path: PATHS.SIGNUPDOCTOR,
    name: 'signup-doctor',
    component: SignUpDoctorApp,
    meta: { layout: 'empty' }
  },
  {
    path: PATHS.DASHBOARD,
    name: 'dashboard',
    // Dashboard mensal (Setor/Especialidade, lentes, Mês e Calendário) atrás da flag;
    // import lazy para que o legado não carregue o caminho V2.
    component: useFeatureFlags().flags.enabledDashboardV2
      ? () => import('@/modules/company/Dashboard/IndexV2.vue')
      : Dashboard,
    meta: {
      requiresCompanyUserAuth: true,
      authorizations: [...StationBasicReader],
      fullHeight: true,
      hideFooter: true
    }
  },
  ...(useFeatureFlags().flags.enabledDashboardVacancyCenter ? [vacancyCenterRoute] : []),
  ...(useFeatureFlags().flags.enabledDashboardDemand ? demandRoutes : []),
  ...(useFeatureFlags().flags.enabledDashboardMap ? [unitMapTvRoute] : []),
  ...(useFeatureFlags().flags.enabledAllocationInvite ? [allocationInviteAcceptRoute] : []),
  ...(useFeatureFlags().flags.enabledAnnouncements ? announcementRoutes : []),
  ...(useFeatureFlags().flags.enabledTerms ? termsRoutes : []),
  {
    path: PATHS.NEW_COMPONENTS,
    name: 'new-components',
    component: NewComponents,
    meta: {
      requiresCompanyUserAuth: true,
      authorizations: [...ScheduleBasicReader, ...HealthUnitBasicReader, ...StationBasicReader]
    }
  },
  {
    path: PATHS.MANAGE_SCALE,
    name: 'gerenciar-escala',
    component: ManageScale,
    meta: { requiresCompanyUserAuth: true, authorizations: [...ScheduleBasicReader] }
  },
  {
    path: '/criar-escala',
    name: 'criar-escala',
    component: CreateSchedule,
    meta: { requiresCompanyUserAuth: true, authorizations: [...ScheduleContentAdmin] }
  },
  ...(useFeatureFlags().flags.enabledPostStation ? [postStationRoute] : []),
  {
    path: PATHS.UNITS,
    name: 'unidades',
    component: () => import('@/modules/company/Units/Index.vue'),
    meta: {
      requiresCompanyUserAuth: true,
      authorizations: [...HealthUnitBasicReader]
    }
  },
  // Antes de UNIT_DETAIL: `/unidades/revisao-pino` casaria com `/unidades/:healthUnitId`.
  {
    path: PATHS.UNIT_PIN_REVIEW,
    name: 'unit-pin-review',
    component: () => import('@/modules/company/Units/PinReview.vue'),
    meta: {
      requiresCompanyUserAuth: true,
      authorizations: [...HealthUnitEditor]
    }
  },
  {
    path: PATHS.UNIT_DETAIL,
    name: 'unit-detail',
    component: () => import('@/modules/company/Units/Detail.vue'),
    meta: {
      requiresCompanyUserAuth: true,
      authorizations: [...HealthUnitBasicReader]
    }
  },
  {
    path: PATHS.CONSULT,
    name: 'consultar',
    component: Consult,
    meta: {
      requiresCompanyUserAuth: true,
      authorizations: [...StationAdvancedReader]
    }
  },
  {
    path: PATHS.DOCTORS,
    name: 'medicos',
    component: Doctors,
    meta: { requiresCompanyUserAuth: true, authorizations: [...StationAdvancedReader] }
  },
  {
    path: PATHS.DOCTOR_DETAIL,
    name: 'doctor-detail',
    component: () => import('@/modules/company/Doctors/Detail.vue'),
    meta: { requiresCompanyUserAuth: true, authorizations: [...StationAdvancedReader] }
  },
  {
    path: PATHS.POLICIES_HOME,
    name: 'politicas-home',
    component: PolicyLanding,
    meta: {
      requiresCompanyUserAuth: true,
      authorizations: [...ScheduleAdvancedReader, ...PostStationPolicies]
    }
  },
  {
    path: PATHS.POLICIES,
    name: 'politicas',
    component: Policies,
    meta: { requiresCompanyUserAuth: true, authorizations: [...ScheduleAdvancedReader] }
  },
  {
    path: PATHS.QUALIFICATION_DOC,
    name: 'qualificacao-documental',
    component: QualificationDocumental,
    meta: {
      requiresCompanyUserAuth: true,
      authorizations: [...ScheduleAdvancedReader, ...PostStationPolicies],
      pageTitle: 'Qualificação Documental'
    }
  },
  {
    path: PATHS.MANAGE_QUALIFICATION_DOC + '/:policyId',
    name: 'editar-qualificacao-documental',
    component: () => import('@/modules/company/Policy/QualificationDocumental/EditPolicy.vue'),
    meta: {
      requiresCompanyUserAuth: true,
      authorizations: [...ScheduleAdvancedReader, ...PostStationPolicies],
      pageTitle: 'Editar Política'
    }
  },
  {
    path: PATHS.QUALIFICATION_TRANSFER,
    name: 'qualificacao-repasse',
    component: QualificationTransfer,
    meta: {
      requiresCompanyUserAuth: true,
      authorizations: [...ScheduleAdvancedReader, ...PostStationPolicies],
      pageTitle: 'Qualificação para Repasse'
    }
  },
  {
    path: PATHS.MANAGE_QUALIFICATION_TRANSFER + '/:policyId',
    name: 'editar-qualificacao-repasse',
    component: () => import('@/modules/company/Policy/QualificationTransfer/EditPolicy.vue'),
    meta: {
      requiresCompanyUserAuth: true,
      authorizations: [...ScheduleAdvancedReader, ...PostStationPolicies],
      pageTitle: 'Editar Política'
    }
  },
  {
    path: PATHS.IMPORT_VALIDATIONS,
    name: 'importar-validacoes',
    component: ComingSoon,
    meta: {
      requiresCompanyUserAuth: true,
      authorizations: [...ScheduleAdvancedReader, ...PostStationPolicies],
      pageTitle: 'Importar Validações'
    }
  },
  {
    path: PATHS.AUDITORIUM_EXCEPTIONALITIES,
    name: 'AuditoriumExceptionalities',
    component: AuditoriumExceptionalities,
    meta: { requiresCompanyUserAuth: true, authorizations: [...StationAdvancedReader] }
  },
  {
    path: PATHS.APPROVALS,
    name: 'Approvals',
    component: Approvals,
    meta: {
      requiresCompanyUserAuth: true,
      // Quem tem o grupo "Documentação" (Leitor ou acima, D112) pode não ter papel na Gestão de
      // Escalas: entra pelo card da Documentação. Só com a flag, senão o menu e a home apareceriam
      // para ele (SPEC §10).
      authorizations: [
        ...DsAttendanceQueueReader,
        ...(useFeatureFlags().flags.enabledProfessionalDocuments ? DoctorDocumentReader : [])
      ]
    },
    // Links antigos `/aprovacoes?tab=x` → rota da fila (só com o hub ligado).
    beforeEnter: (to) => resolveApprovalsTabRedirect(to.query, useFeatureFlags().flags) ?? true
  },
  // Uma rota por fila de aprovação (hub em cards). `meta.approvalsQueue` é a chave
  // do registro em Approvals/Hub/queues.ts; a página resolve título e componente.
  approvalsQueueRoute(PATHS.APPROVALS_HANDOVERS, 'ApprovalsHandovers', 'handoverApprovals'),
  approvalsQueueRoute(
    PATHS.APPROVALS_EXCEPTIONALITIES,
    'ApprovalsExceptionalities',
    'exceptionality'
  ),
  approvalsQueueRoute(PATHS.APPROVALS_DS_ATTENDANCE, 'ApprovalsDsAttendance', 'attendance'),
  approvalsQueueRoute(
    PATHS.APPROVALS_PRIORITY_PAYMENTS,
    'ApprovalsPriorityPayments',
    'priorityPayments'
  ),
  approvalsQueueRoute(
    PATHS.APPROVALS_CHECK_VALIDATION,
    'ApprovalsCheckValidation',
    'checkValidation'
  ),
  approvalsQueueRoute(PATHS.APPROVALS_FACIAL_BIOMETRY, 'ApprovalsFacialBiometry', 'facialBiometry'),
  // Só registrada com a flag: desligada, a rota (autorizada só ao revisor) viraria destino do
  // fallback de acesso negado e o guard a devolveria a uma home que ele não abre (loop).
  ...(useFeatureFlags().flags.enabledProfessionalDocuments
    ? [
        approvalsQueueRoute(
          PATHS.APPROVALS_PROFESSIONAL_DOCUMENTS,
          'ApprovalsProfessionalDocuments',
          'professionalDocuments'
        )
      ]
    : []),
  {
    path: PATHS.HANDOVER_EVALUATION,
    name: 'HandoverEvaluation',
    component: HandoverEvaluation,
    meta: { requiresCompanyUserAuth: true, authorizations: [...StationAdvancedReader] }
  },
  {
    path: PATHS.HANDOVER_DETAILS,
    name: 'HandoverDetails',
    component: HandoverDetails,
    meta: { requiresCompanyUserAuth: true, authorizations: [...StationAdvancedReader] }
  },
  {
    path: PATHS.DS_ATTENDANCE_PENDING_AUDIT,
    name: 'DsAttendancePendingAudit',
    component: DsAttendanceAwaitingAudit,
    meta: { requiresCompanyUserAuth: true, authorizations: [...DsAttendanceQueueReader] }
  },
  {
    path: PATHS.DS_ATTENDANCE_PENDING_SUBMISSION,
    name: 'DsAttendancePendingSubmission',
    component: DsAttendanceAwaitingShipment,
    meta: { requiresCompanyUserAuth: true, authorizations: [...DsAttendanceQueueReader] }
  },
  {
    path: PATHS.DS_ATTENDANCE_APPROVED,
    name: 'DsAttendanceApproved',
    component: DsAttendanceAwaitingAudit,
    meta: { requiresCompanyUserAuth: true, authorizations: [...DsAttendanceQueueReader] }
  },
  {
    path: PATHS.MANAGE_DOCTOR_AUTHORIZATION_POLICIES + '/:ds_policy_id',
    name: 'gerenciar-politica-de-autorizacao-medica',
    component: ManageDoctorAuthorizationPolicie,
    meta: { requiresCompanyUserAuth: true, authorizations: [...ScheduleAdvancedReader] }
  },
  {
    path: PATHS.USERS,
    name: 'usuarios',
    component: Users,
    meta: { requiresCompanyUserAuth: true, authorizations: [...StationAdvancedReader] }
  },
  ...(useFeatureFlags().flags.enabledManagement ? [managementRoute] : []),
  {
    path: PATHS.PROFILE,
    name: 'perfil',
    component: Profile,
    meta: { requiresCompanyUserAuth: true, isAdmin: false, authorizations: [...StationBasicReader] }
  },
  {
    path: PATHS.ACCESS_MANAGER,
    name: 'gerenciar-acesso',
    component: AccessManager,
    meta: { requiresCompanyUserAuth: true, authorizations: [...CompanyGeneralAdmin] }
  },
  {
    path: PATHS.REPORTS,
    name: 'relatorios',
    component: Reports,
    meta: { requiresCompanyUserAuth: true, authorizations: [...ScheduleAdvancedReader] }
  },
  {
    path: PATHS.NPS_DASHBOARD,
    name: 'avaliacao-medica',
    component: () => import('@/modules/company/NPS/Index.vue'),
    meta: { requiresCompanyUserAuth: true, authorizations: [...NpsReader, ...NpsAdmin] }
  },
  {
    path: PATHS.NPS_SETTINGS,
    name: 'nps-settings',
    component: () => import('@/modules/company/NPS/Settings/Index.vue'),
    meta: { requiresCompanyUserAuth: true, authorizations: [...NpsAdmin] }
  },
  {
    path: PATHS.NPS_EVALUATION,
    name: 'nps-evaluation',
    component: () => import('@/modules/company/NPS/Evaluation/Index.vue'),
    meta: { requiresCompanyUserAuth: true, authorizations: [...NpsEvaluator, ...NpsAdmin] }
  },
  {
    path: PATHS.NPS_EDITOR,
    name: 'nps-editor',
    component: () => import('@/modules/company/NPS/components/enquetes/SurveyEditor.vue'),
    meta: { requiresCompanyUserAuth: true, authorizations: [...NpsAdmin], layout: 'empty' }
  },
  {
    path: PATHS.NPS_PUBLIC_FORM,
    name: 'NpsPublicForm',
    component: () => import('@/modules/public/NpsForm/Index.vue'),
    meta: { layout: 'empty' }
  },
  {
    path: PATHS.NPS_PUBLIC_FORM_LINK,
    name: 'NpsPublicFormLink',
    component: () => import('@/modules/public/NpsForm/Index.vue'),
    meta: { layout: 'empty' }
  },
  {
    path: PATHS.NPS_ANSWER_DETAILS,
    name: 'nps-answer-details',
    component: () => import('@/modules/company/NPS/components/dashboard/ViewAnswers.vue'),
    meta: {
      requiresCompanyUserAuth: true,
      authorizations: [...NpsReader, ...NpsAdmin],
      layout: 'empty'
    }
  },
  // Backoffice: fora do menu, alcancado so por link direto. A tela tolera
  // backend sem a rota da F1 (404 -> "recurso indisponivel"), entao publica-la
  // antes do deploy do backend nao quebra nada.
  {
    path: PATHS.BACKOFFICE_EFFECTIVE_CHECK_RECOMPUTE,
    name: 'backoffice-recalculo-horario-efetivo',
    component: () => import('@/modules/company/Backoffice/EffectiveCheckRecompute/Index.vue'),
    meta: {
      requiresCompanyUserAuth: true,
      authorizations: [...EffectiveCheckRecalculator],
      // `findFirstAccessibleRoute` varre `routes` em ordem no fallback de acesso
      // negado. Sem esta marca, um usuario cujo unico casamento fosse
      // `attendance_general_administrating` cairia AUTOMATICAMENTE na tela que
      // reescreve horario efetivo em massa — o oposto de "so por link direto".
      excludeFromAccessFallback: true
    }
  },
  {
    path: PATHS.CHANGELOG,
    name: 'changelog',
    component: () => import('@/modules/company/Changelog/Index.vue'),
    meta: { requiresCompanyUserAuth: true }
  },
  // Portal do Afiliado Antecipe + Gestão › Afiliados: só com a flag de build.
  ...(useFeatureFlags().flags.enabledAntecipeAffiliate ? affiliateRoutes : []),
  {
    path: PATHS.NOT_AUTHORIZED,
    name: 'not-authorized',
    component: NotAuthorized,
    meta: { layout: 'empty' }
  },
  {
    // Sem `requiresCompanyUserAuth`: o guard que redireciona para cá não pode
    // rodar de novo nela (loop).
    path: PATHS.PERMISSIONS_UNAVAILABLE,
    name: PERMISSIONS_UNAVAILABLE_ROUTE_NAME,
    component: PermissionsUnavailable,
    meta: { layout: 'empty' }
  },
  {
    path: '/politicas-de-autorizacao-de-medicos',
    redirect: (to) => ({ path: PATHS.POLICIES, query: to.query })
  },
  {
    path: '/gerenciar-politica-de-autorizacao-medica/:ds_policy_id',
    redirect: (to) => `${PATHS.MANAGE_DOCTOR_AUTHORIZATION_POLICIES}/${to.params.ds_policy_id}`
  },
  {
    path: '/:pathMatch(.*)*',
    name: NOT_FOUND_ROUTE_NAME,
    component: NotFound,
    // layout resolvido em runtime pelo App.vue (autenticado → default, anônimo → empty)
    meta: { pageTitle: 'Página não encontrada' }
  }
]

export type UserContext = 'company' | 'doctor' | 'affiliate' | ''

export function setUserContextInLocalStorage(context: UserContext) {
  localStorage.setItem('@ME:user_context', context)
}

function isAuthenticated() {
  return isCompanyAuthenticated()
}

export const router = createRouter({ routes, history })

const logout = (authStore) => {
  authStore.logout()
  router.push({ name: 'signin' })
}

function findFirstAccessibleRoute(
  permissionsStore: ReturnType<typeof usePermissionsStore>
): string | null {
  for (const route of routes) {
    if (!route.meta?.requiresCompanyUserAuth) continue
    if (route.path.includes(':')) continue
    // Telas de backoffice nunca sao destino de redirecionamento automatico.
    if (route.meta?.excludeFromAccessFallback) continue

    const authorizations = route.meta?.authorizations
    if (!authorizations?.length) continue

    const canAccess = authorizations.some((authorization) => {
      return permissionsStore.checkAnyRessources(authorization)
    })

    if (canAccess) return route.path
  }

  return null
}

// eslint-disable-next-line sonarjs/cognitive-complexity
router.beforeResolve(async (to, from, next) => {
  const authStore = useAuthStore()
  const permissionsStore = usePermissionsStore()

  const affiliateDecision = resolveAffiliateGuard(to.meta, {
    affiliateAuthenticated: isAffiliateAuthenticated(),
    companyAuthenticated: isAuthenticated(),
    enabled: useFeatureFlags().flags.enabledAntecipeAffiliate
  })
  if (affiliateDecision.action === 'redirect') {
    next(affiliateDecision.to)
    return
  }
  if (affiliateDecision.action === 'next') {
    setUserContextInLocalStorage('affiliate')
    next()
    return
  }

  if (to.meta.requiresCompanyUserAuth) {
    setUserContextInLocalStorage('company')

    if (isAuthenticated()) {
      await permissionsStore.fetchPermissions()

      // A carga falhou (rede/timeout/5xx): não é "sem acesso", então não vai para
      // /not-authorized. E a rota de destino nunca é liberada — nem as que não
      // declaram `authorizations`, que abririam com a tela inteira escondida.
      if (permissionsStore.loadError) {
        next({ name: PERMISSIONS_UNAVAILABLE_ROUTE_NAME, query: { redirect: to.fullPath } })
        return
      }

      if (to.meta.authorizations?.length) {
        const canAccess = to.meta.authorizations.some((authorization) => {
          return permissionsStore.checkAnyRessources(authorization)
        })

        if (!canAccess) {
          console.warn(`Acesso negado para a rota: ${to.path}. Permissões insuficientes.`)

          const fallbackPath = findFirstAccessibleRoute(permissionsStore)

          if (fallbackPath && fallbackPath !== to.path) {
            next(fallbackPath)
          } else {
            next(PATHS.NOT_AUTHORIZED)
          }
          return
        }

        next()
      } else {
        next()
      }
    } else {
      logout(authStore)
    }
  } else {
    next()
  }
})

// Microsoft Clarity: só dispara para usuário-empresa autenticado.
// 1) Guard SSR primeiro — afterEach roda no server (rotas públicas) e localStorage
//    é undefined lá (beforeResolve só toca localStorage atrás de meta.requires*).
// 2) Exclusão de médico como invariante EXPLÍCITA: user_context === 'company'
//    (médico = 'doctor') + isAuthenticated() (checa @ME:access_token, que o médico
//    não possui — usa @ME:doctor_access_token). Cobre login fresco e reload-autenticado.
router.afterEach((to) => {
  if (isSSR) return
  // Rota com segredo na URL (Modo TV): encerra a captura já iniciada nesta aba.
  if (to.meta.skipAnalytics) {
    stopClarity()
    return
  }
  if (!isAuthenticated() || localStorage.getItem('@ME:user_context') !== 'company') return

  initClarity()
  identifyUser(Number(localStorage.getItem('@ME:userId')))
  trackPage(to.name, to.path)
})

// Clássica do dashboard no celular (spec v2 T8): fora do /dashboard o viewport volta ao original.
// Navegação falhada/abortada não saiu do /dashboard: não restaura.
router.afterEach((to, _from, failure) => {
  if (isSSR || failure) return
  restoreViewportOutside(to.path, PATHS.DASHBOARD)
})

// Versão nova publicada: troca de tela também confere o /version.json (com throttle).
router.afterEach((_to, _from, failure) => {
  if (isSSR || failure) return
  void useAppVersionCheck().checkIfStale()
})

// Chunk da rota sumiu do servidor (deploy novo com a aba aberta): confirmado no
// /version.json, carrega o destino do zero. Falha de rede segue como erro comum.
router.onError((error, to) => {
  if (isSSR || !isChunkLoadError(error)) return
  void useAppVersionCheck().recoverFromChunkLoadError(to.fullPath)
})
